/**
 * Harmonic Drift - Physics Engine (Enhanced)
 * Handles collision detection, resolution, and melodic collision notifications
 */

import { CONFIG } from '../config/index.js';

class PhysicsEngine {
    constructor() {
        this.paused = false;
    }

    /**
     * Resolve collisions between all nodes
     * Enhanced with collision notification for melodic harmony
     * @param {Array<Node>} nodes - Array of nodes
     */
    resolveCollisions(nodes) {
        if (this.paused) return;

        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const p1 = nodes[i];
                const p2 = nodes[j];
                
                // Skip locked or fixed nodes
                if (p1.locked || p2.locked || p1.isFixed || p2.isFixed) continue;
                
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const minDist = p1.radius + p2.radius;
                
                if (dist < minDist) {
                    // Calculate collision intensity based on relative velocity
                    const relativeVx = p2.vx - p1.vx;
                    const relativeVy = p2.vy - p1.vy;
                    const relativeSpeed = Math.sqrt(relativeVx * relativeVx + relativeVy * relativeVy);
                    
                    // Separate the nodes
                    const angle = Math.atan2(dy, dx);
                    const overlap = (minDist - dist) * CONFIG.physics.collisionResponse;
                    const mx = Math.cos(angle) * overlap;
                    const my = Math.sin(angle) * overlap;
                    
                    p1.x -= mx;
                    p1.y -= my;
                    p2.x += mx;
                    p2.y += my;
                    
                    // Exchange velocities (simple elastic collision)
                    const tvx = p1.vx;
                    const tvy = p1.vy;
                    p1.vx = p2.vx;
                    p1.vy = p2.vy;
                    p2.vx = tvx;
                    p2.vy = tvy;
                    
                    // === ENHANCED: Notify nodes about collision for melodic harmony ===
                    if (CONFIG.melodicPhysics && CONFIG.melodicPhysics.collisionHarmonyEnabled) {
                        // Only trigger harmony for significant collisions
                        if (relativeSpeed > 0.5) {
                            this.notifyCollision(p1, p2, relativeSpeed);
                        }
                    }
                }
            }
        }
    }

    /**
     * Notify nodes about a collision for melodic harmony generation
     * @param {Node} node1 - First colliding node
     * @param {Node} node2 - Second colliding node
     * @param {number} intensity - Collision intensity (relative speed)
     */
    notifyCollision(node1, node2, intensity) {
        // Check if nodes have the collision registration method
        if (typeof node1.registerCollision === 'function') {
            node1.registerCollision(node2, intensity);
        }
        if (typeof node2.registerCollision === 'function') {
            node2.registerCollision(node1, intensity);
        }
    }

    /**
     * Toggle physics pause state
     * @returns {boolean} New paused state
     */
    togglePause() {
        this.paused = !this.paused;
        return this.paused;
    }

    /**
     * Set physics pause state directly
     * @param {boolean} state - The new pause state
     */
    setPausedState(state) {
        this.paused = state;
    }

    /**
     * Check if physics is paused
     * @returns {boolean}
     */
    isPaused() {
        return this.paused;
    }

    /**
     * Set physics pause state
     * @param {boolean} paused - Whether physics should be paused
     */
    setPaused(paused) {
        this.paused = paused;
    }

    /**
     * Check if physics is paused
     * @returns {boolean} Whether physics is paused
     */
    isPaused() {
        return this.paused;
    }
}

// Export singleton instance
export const physicsEngine = new PhysicsEngine();